import { dynamicManager } from 'pages/FormProcess/DynamicForm/DynamicManager';
import { useDispatch, useSelector } from 'react-redux';

import {
  addDynamicFormCommentEffect,
  deleteDynamicFormCommentEffect,
  replyDynamicFormCommentEffect,
  resolveDynamicFormCommentEffect,
  updateDynamicFormCommentEffect,
} from 'store/effects/formProcess';

import { formCommentSelector } from 'store/selectors/formComment';
import { getDynamicFormSelector } from 'store/selectors/requestFormProcess';
import { AddFormCommentType, EditFormCommentType } from 'types';
import { prepareExistingCommentPayload, prepareNewCommentPayload } from '../helper';

export const useManageFormComments = () => {
  const dispatch = useDispatch();

  const { dynamicFormsComments = [], formsComments = {} } = useSelector(getDynamicFormSelector);
  const { commentUsers } = useSelector(formCommentSelector);

  const onAddFormComment = (
    payload: AddFormCommentType,
    setSending: (isSending: boolean) => void,
  ) => {
    setSending(true);

    const { isNewThread, meta } = payload;

    if (isNewThread) {
      const modifiedPayload = prepareNewCommentPayload({
        participants: Object.keys(commentUsers)
          .map((id) => +id)
          .filter((id) => !!id),
        ...payload,
      });

      dispatch(
        addDynamicFormCommentEffect(modifiedPayload, (error) => {
          setSending(false);
          if (!error) {
            dynamicManager.getDynamicFormComments('', true);
          }
        }),
      );
    } else {
      const commentPayload = prepareExistingCommentPayload(payload);

      const threadId =
        isNewThread && meta?.questionUUID
          ? formsComments[meta?.questionUUID].find(({ Resolved }) => !Resolved)?.Id
          : payload.threadId;

      dispatch(
        replyDynamicFormCommentEffect({ threadId, ...commentPayload }, (error, response) => {
          if (!error) {
            dynamicManager.getDynamicFormComments('', true);
          }
          setSending(false);
        }),
      );
    }
  };

  const onEditFormComment = async (
    payload: EditFormCommentType,
    setEditingMode: (editingMode: boolean) => void,
    setSending: (isSending: boolean) => void,
  ) => {
    const commentPayload = prepareExistingCommentPayload(payload);
    setSending(true);

    dispatch(
      updateDynamicFormCommentEffect(
        { ...commentPayload, commentId: payload.formCommentId },
        (error) => {
          if (!error) {
            dynamicManager.getDynamicFormComments('', true);
            setEditingMode(false);
          }
          setSending(false);
        },
      ),
    );
  };

  const onDeleteFormComment = async (formCommentId: number) => {
    dispatch(
      deleteDynamicFormCommentEffect({ CommentId: formCommentId }, (error) => {
        if (!error) {
          dynamicManager.getDynamicFormComments('', true);
        }
      }),
    );
  };

  const onResolveFormThread = async (
    threadId: number,
    setResolving: (resolving: boolean) => void,
  ) => {
    dispatch(
      resolveDynamicFormCommentEffect({ threadId }, (error, response) => {
        if (!error) {
          dynamicManager.getDynamicFormComments('', true);
        }
        setResolving(false);
      }),
    );
  };

  return {
    commentActions: {
      handleAddFormComment: onAddFormComment,
      handleEditFormComment: onEditFormComment,
      handleDeleteFormComment: onDeleteFormComment,
      handleToggleResolveThread: onResolveFormThread,
    },

    commentUsers,
    dynamicFormsComments,
    formsComments,
  };
};
